import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRemoteContent } from "@/lib/ContentContext";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const accentMap: Record<string, { text: string; border: string; bg: string; glow: string; ring: string }> = {
  cyan: { text: "text-cyan-400", border: "border-cyan-500/40", bg: "bg-cyan-500/10", glow: "from-cyan-500", ring: "ring-cyan-500/40" },
  orange: { text: "text-orange-400", border: "border-orange-500/40", bg: "bg-orange-500/10", glow: "from-orange-500", ring: "ring-orange-500/40" },
  emerald: { text: "text-teal-400", border: "border-teal-500/40", bg: "bg-teal-500/10", glow: "from-teal-500", ring: "ring-teal-500/40" },
  violet: { text: "text-violet-400", border: "border-violet-500/40", bg: "bg-violet-500/10", glow: "from-violet-500", ring: "ring-violet-500/40" },
};

const categoryIcons: Record<string, string> = {
  "aero-engine": "ri-fire-line",
  "uav-propulsion": "ri-flight-takeoff-line",
  "precision-mfg": "ri-settings-3-line",
  "tech-services": "ri-flask-line",
};

function parseDescriptionParts(text: string): Array<{ kind: "text"; value: string } | { kind: "link"; label: string; url: string }> {
  const pattern = /(\[[^\]]+\]\((https?:\/\/[^)\s]+)\))|(https?:\/\/[^\s]+)/g;
  const parts: Array<{ kind: "text"; value: string } | { kind: "link"; label: string; url: string }> = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ kind: "text", value: text.slice(last, match.index) });
    }
    const full = match[0];
    if (full.startsWith("[")) {
      const labelMatch = full.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (labelMatch) {
        parts.push({ kind: "link", label: labelMatch[1], url: labelMatch[2] });
      } else {
        parts.push({ kind: "text", value: full });
      }
    } else {
      parts.push({ kind: "link", label: full, url: full });
    }
    last = match.index + full.length;
  }

  if (last < text.length) {
    parts.push({ kind: "text", value: text.slice(last) });
  }

  return parts;
}

function getVideoEmbedUrl(url: string): string | null {
  if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(url)) return url;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const bili = url.match(/bilibili\.com\/video\/(BV[0-9A-Za-z]+)/);
  if (bili) return `https://player.bilibili.com/player.html?bvid=${bili[1]}&page=1`;
  return null;
}

function ProductDescriptionRich({ text }: { text: string }) {
  const parts = parseDescriptionParts(text);
  const videoLinks = parts
    .filter((part): part is { kind: "link"; label: string; url: string } => part.kind === "link")
    .map((item) => ({ ...item, embed: getVideoEmbedUrl(item.url) }))
    .filter((item) => Boolean(item.embed));

  return (
    <div className="mb-8 space-y-4">
      <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap break-words">
        {parts.map((part, idx) => {
          if (part.kind === "text") return <span key={idx}>{part.value}</span>;
          return (
            <a
              key={idx}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-300 underline underline-offset-2 hover:text-cyan-200"
            >
              {part.label}
            </a>
          );
        })}
      </p>

      {videoLinks.length > 0 && (
        <div className="space-y-3">
          {videoLinks.map((item, i) => (
            <div key={`${item.url}-${i}`} className="rounded-xl border border-cyan-500/25 bg-white/5 p-3">
              <p className="text-cyan-300 text-xs mb-2">视频预览：{item.label}</p>
              {/\.(mp4|webm|ogg)(\?|#|$)/i.test(item.url) ? (
                <video src={item.url} controls className="w-full rounded-lg" />
              ) : (
                <iframe
                  src={item.embed ?? undefined}
                  title={`video-${i}`}
                  className="w-full aspect-video rounded-lg border border-white/10"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function sanitizeFrontendRichHtml(html: string) {
  if (!html.includes("data-media-drag-handle") && !html.includes("data-media-resize-handle")) return html;
  if (typeof document === "undefined") return html;
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("[data-media-drag-handle='1'], [data-media-resize-handle='1']").forEach((node) => node.remove());
  return container.innerHTML;
}

// ─── Image Gallery Components ───────────────────────────────────────────────

function ProductMainImage({ productId, image, alt, active }: { productId: string; image: string; alt: string; active: boolean }) {
  return (
    <img
      src={image}
      alt={alt}
      className="w-full h-full object-cover object-top"
      style={{ animation: "fadeIn 0.4s ease" }}
    />
  );
}

function ProductThumbImage({ productId, image, index, alt }: { productId: string; image: string; index: number; alt: string }) {
  return (
    <img src={image} alt={alt} className="w-full h-full object-cover object-top" />
  );
}

function RelatedProductCard({ product, lang }: { product: { id: string; images: string[]; zh: { title: string; subtitle: string }; en: { title: string; subtitle: string }; accent: string }; lang: "zh" | "en" }) {
  const navigate = useNavigate();
  const rt = lang === "zh" ? product.zh : product.en;
  const ra = accentMap[product.accent] || accentMap.cyan;

  return (
    <div
      onClick={() => navigate(`/products/${product.id}`)}
      className={`group rounded-xl border ${ra.border} bg-[#0d1428] overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-300`}
    >
      <div className="relative w-full aspect-[16/9] overflow-hidden">
        <img
          src={product.images[0]}
          alt={rt.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-xh-bg-card to-transparent" />
      </div>
      <div className="p-4">
        <p className="text-gray-500 text-xs mb-1">{rt.subtitle}</p>
        <h3 className={`text-sm font-bold mb-2 ${ra.text}`}>{rt.title}</h3>
        <div className={`flex items-center gap-1.5 text-xs font-semibold ${ra.text}`}>
          <span>{lang === "zh" ? "查看详情" : "View Details"}</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lang, setLang] = useState<"zh" | "en">("zh");
  const [activeImg, setActiveImg] = useState(0);
  const [mounted, setMounted] = useState(false);

  const siteContent = useRemoteContent();
  const products = siteContent.productsDetail;
  const techServiceDocs = [...siteContent.techServiceDocs].sort((a, b) => a.order - b.order);
  const productCategories = siteContent.productCategories;
  const navContent = siteContent.nav;

  const product = products.find((p) => p.id === id);

  useEffect(() => {
    setMounted(true);
    const storedLang = localStorage.getItem("site-lang");
    if (storedLang === "en" || storedLang === "zh") setLang(storedLang as "zh" | "en");
    setActiveImg(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const title = `${product.zh.title} - ${product.zh.subtitle} | 星核动力`;
    document.title = title;
    const metaDesc = document.querySelector<HTMLMetaElement>("meta[name='description']");
    if (metaDesc) metaDesc.content = `${product.zh.title}：${product.zh.desc.slice(0, 120)}`;
    const catName = productCategories.find((c) => c.id === product.category)?.zh ?? "";
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.zh.title,
      alternateName: product.en.title,
      description: product.zh.desc,
      image: product.images,
      url: `${SITE_URL}/products/${product.id}`,
      brand: { "@type": "Brand", name: "星核动力科技" },
      category: catName,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    script.setAttribute("data-schema", `product-${product.id}`);
    document.head.appendChild(script);
    return () => {
      script.parentNode?.removeChild(script);
      document.title = "星核动力 | 航空发动机·无人机动力·精密制造";
    };
  }, [product, productCategories]);

  if (!product) {
    return (
      <div className="min-h-screen bg-xh-bg flex flex-col items-center justify-center text-white">
        <i className="ri-error-warning-line text-5xl text-gray-600 mb-4" />
        <p className="text-gray-400 mb-6">产品未找到</p>
        <button onClick={() => navigate("/products")} className="px-5 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm cursor-pointer">
          返回产品列表
        </button>
      </div>
    );
  }

  const a = accentMap[product.accent] || accentMap.cyan;
  const t = lang === "zh" ? product.zh : product.en;
  const catName = lang === "zh"
    ? productCategories.find((c) => c.id === product.category)?.zh
    : productCategories.find((c) => c.id === product.category)?.en;
  const catIcon = categoryIcons[product.category] ?? "ri-apps-line";

  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);
  const isTechServices = product.category === "tech-services";

  return (
    <div className="min-h-screen bg-xh-bg text-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-xh-bg-panel/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/products")}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              <span className="text-sm">{lang === "zh" ? "产品列表" : "Products"}</span>
            </button>
            <span className="text-gray-700 text-sm">·</span>
            <span className={`text-sm font-medium ${a.text} whitespace-nowrap hidden sm:block`}>{t.title}</span>
          </div>
          <img
            src={navContent.logoImage}
            alt={navContent.logoAlt}
            className="h-9 w-auto object-contain cursor-pointer"
            onClick={() => navigate("/")}
          />
          <button
            onClick={() => {
              const nl = lang === "zh" ? "en" : "zh";
              setLang(nl);
              localStorage.setItem("site-lang", nl);
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full border ${a.border} ${a.text} text-xs font-semibold hover:${a.bg} transition-all duration-300 whitespace-nowrap cursor-pointer`}
          >
            <i className="ri-translate-2 text-sm" />
            {lang === "zh" ? "EN" : "中文"}
          </button>
        </div>
      </nav>

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16 relative z-10">
          {/* Breadcrumb */}
          <div className={`flex items-center gap-2 text-xs text-gray-500 mb-8 transition-all duration-500 ${mounted ? "opacity-100" : "opacity-0"}`}>
            <button onClick={() => navigate("/")} className="hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap">
              {lang === "zh" ? "首页" : "Home"}
            </button>
            <i className="ri-arrow-right-s-line" />
            <button onClick={() => navigate("/products")} className="hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap">
              {lang === "zh" ? "产品中心" : "Products"}
            </button>
            <i className="ri-arrow-right-s-line" />
            <button
              onClick={() => navigate(`/products?cat=${product.category}`)}
              className={`hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap ${a.text}`}
            >
              {catName}
            </button>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-400">{t.title}</span>
          </div>

          {/* Main Content */}
          <div className={`grid grid-cols-1 ${isTechServices ? "" : "lg:grid-cols-2 gap-10 lg:gap-16"} mb-16`}>
            {/* Left: Image Gallery */}
            {!isTechServices && (
            <div className={`transition-all duration-700 ${mounted ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
              <div className={`relative rounded-2xl overflow-hidden border ${a.border} mb-4 bg-xh-bg-card aspect-[3/2]`}>
                <ProductMainImage
                  productId={product.id}
                  image={product.images[activeImg]}
                  alt={`${t.title} - view ${activeImg + 1}`}
                  active={true}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-xh-bg-card/40 to-transparent pointer-events-none" />
                  <div className={`absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full border ${a.border} ${a.bg} backdrop-blur-sm`}>
                    <i className={`ri-box-3-line text-sm ${a.text}`} />
                    <span className={`text-xs font-semibold ${a.text}`}>3D View</span>
                  </div>
                  {product.images.length > 1 && (
                    <div className="absolute bottom-4 right-4 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-gray-300 text-xs">
                      <span className={a.text}>{activeImg + 1}</span>
                      <span>/</span>
                      <span>{product.images.length}</span>
                    </div>
                  )}
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setActiveImg((prev) => (prev - 1 + product.images.length) % product.images.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-all cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line" />
                      </button>
                      <button
                        onClick={() => setActiveImg((prev) => (prev + 1) % product.images.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-all cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </>
                  )}
                </div>

              {product.images.length > 1 && (
                <div className="flex gap-3">
                  {product.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`relative flex-1 rounded-xl overflow-hidden border-2 transition-all duration-300 cursor-pointer aspect-[3/2] ${
                        activeImg === i ? `${a.border} ring-2 ${a.ring}` : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <ProductThumbImage
                        productId={product.id}
                        image={img}
                        index={i}
                        alt={`${t.title} thumb ${i + 1}`}
                      />
                      {activeImg !== i && <div className="absolute inset-0 bg-black/40" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Right: Product Info */}
            <div className={`transition-all duration-700 delay-150 ${mounted ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${a.border} ${a.bg} mb-4`}>
                <i className={`${catIcon} text-sm ${a.text}`} />
                <span className={`text-xs font-semibold ${a.text}`}>{catName}</span>
              </div>

              <h1 className={`text-2xl md:text-3xl font-bold mb-1.5 ${a.text}`}>{t.title}</h1>
              <p className="text-gray-400 text-sm mb-5">{t.subtitle}</p>

              {!isTechServices && (
                <>
                  {t.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {t.tags.map((tag, i) => (
                        <span key={i} className="px-2.5 py-0.5 rounded-full text-xs bg-white/5 text-gray-400 border border-white/10 whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <ProductDescriptionRich text={t.desc} />

                  {t.specs.length > 0 && (
                    <div className={`rounded-xl border ${a.border} ${a.bg} p-5 mb-6`}>
                      <h3 className="text-white text-sm font-bold mb-4 flex items-center gap-2">
                        <i className={`ri-bar-chart-horizontal-line ${a.text}`} />
                        {lang === "zh" ? "技术规格" : "Technical Specifications"}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {t.specs.map((spec, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-3">
                            <p className="text-gray-500 text-xs mb-1">{spec.label}</p>
                            <p className={`text-sm font-bold ${a.text}`}>{spec.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {t.features.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-white text-sm font-bold mb-3 flex items-center gap-2">
                        <i className={`ri-star-line ${a.text}`} />
                        {lang === "zh" ? "核心优势" : "Key Features"}
                      </h3>
                      <ul className="flex flex-col gap-2.5">
                        {t.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-gray-300 text-sm">
                            <span className={`mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0 rounded-full ${a.bg} ${a.border} border`}>
                              <i className={`ri-check-line text-xs ${a.text}`} />
                            </span>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {isTechServices && (
                <div className="mb-8 space-y-5">
                  <article className="rounded-xl border border-gray-300 bg-white text-black p-6 md:p-8">
                    <h3 className="text-2xl font-bold mb-3">{lang === "zh" ? t.title : t.title}</h3>
                    <p className="text-sm text-gray-600 mb-8">{t.desc}</p>

                    <div className="space-y-8">
                      {techServiceDocs.map((doc) => {
                        const title = lang === "zh" ? doc.zhTitle : doc.enTitle;
                        const summary = lang === "zh" ? doc.zhSummary : doc.enSummary;
                        const html = sanitizeFrontendRichHtml(lang === "zh" ? doc.zhContentHtml : doc.enContentHtml);
                        return (
                          <section key={doc.id} className="border-b border-gray-200 pb-8 last:border-b-0 last:pb-0">
                            <div className="flex items-start gap-3 mb-3">
                              <span className="text-base font-bold text-gray-700 shrink-0">{doc.order}、</span>
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">{title}</h4>
                                <p className="text-sm text-gray-600 mt-1">{summary}</p>
                              </div>
                            </div>
                            <article className="prose prose-sm max-w-none pl-7" dangerouslySetInnerHTML={{ __html: html }} />
                          </section>
                        );
                      })}
                    </div>
                  </article>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/");
                    setTimeout(() => {
                      const el = document.querySelector("#contact");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }, 300);
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap border ${a.border} ${a.text} hover:${a.bg}`}
                >
                  <i className="ri-mail-send-line" />
                  {lang === "zh" ? "联系我们获取报价" : "Contact Us for Quote"}
                </a>
                <button
                  onClick={() => navigate("/products")}
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-gray-400 border border-white/10 hover:border-white/20 hover:text-gray-300 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-2-line" />
                  {lang === "zh" ? "查看更多" : "See More"}
                </button>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {related.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-7">
                <div className={`h-px flex-1 bg-gradient-to-r ${a.glow} to-transparent opacity-30`} />
                <h2 className="text-base font-bold text-white whitespace-nowrap">
                  {lang === "zh" ? "同类产品推荐" : "Related Products"}
                </h2>
                <div className={`h-px flex-1 bg-gradient-to-l ${a.glow} to-transparent opacity-30`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((rp) => (
                  <RelatedProductCard product={rp} lang={lang} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-white/5 py-8 text-center mt-12">
        <p className="text-gray-600 text-xs">
          &copy; 2024 {lang === "zh" ? "星核动力科技有限公司 · 版权所有" : "Xinghe Propulsion Technology Co., Ltd. · All Rights Reserved"}
        </p>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(1.03); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
